import Autocomplete from "@/components/Autocomplete";
import { TypedDocumentNode, gql, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";

type SectionAutocompleteResult = {
  id: string;
  name: string;
  legacyId: string | null;
};

export const SECTION_AUTOCOMPLETE_QUERY: TypedDocumentNode<
  {
    sections: SectionAutocompleteResult[];
  },
  {}
> = gql`
  query sectionAutocompleteQuery {
    sections {
      id
      name
      legacyId
    }
  }
`;

interface SectionAutocompleteProps {
  name: string;
  label: string;
  initialQuery: string;
  disabled?: boolean;
  required?: boolean;
  supportingText?: string;
}

function SectionAutocomplete({
  name,
  label,
  initialQuery,
  disabled = false,
  required = false,
  supportingText
}: SectionAutocompleteProps) {
  const {
    control,
    formState: { errors, isSubmitting }
  } = useFormContext();
  const [query, setQuery] = useState(initialQuery);
  const { data, loading } = useQuery(SECTION_AUTOCOMPLETE_QUERY);
  const sections = useMemo(() => data?.sections || [], [data?.sections]);
  const selectedSectionId = useWatch({ control, name });
  const filteredSections = sections.filter(
    (s) =>
      s.name?.toLowerCase().includes(query.toLowerCase()) || s.legacyId?.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    if (query || !selectedSectionId) return;

    const selectedSection = sections.find((section) => section.id === selectedSectionId);
    if (selectedSection) {
      setQuery(selectedSection.name);
    }
  }, [query, sections, selectedSectionId]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange } }) => (
        <Autocomplete<SectionAutocompleteResult>
          variant="searchableDropdown"
          data={filteredSections}
          loadingResults={loading}
          query={query}
          label={label}
          renderItem={(opt) => <div className="text-body-large">{opt.name}</div>}
          reset={() => {
            onChange("");
            setQuery("");
          }}
          onChangeText={setQuery}
          onSelect={(value) => {
            onChange(value.id);
            setQuery(value.name);
          }}
          disabled={isSubmitting || disabled}
          required={required}
          supportingText={supportingText}
          error={!!errors[name]}
          errorText={errors[name]?.message as string}
        />
      )}
    />
  );
}

export default SectionAutocomplete;
